import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';
import { IconButton } from '@ntconsult/aluma-react';
import { useUpload, type UploadMetadata } from '../hooks/useUpload';
import { UploadDropzone } from './upload/UploadDropzone';
import { UploadMetadataForm } from './upload/UploadMetadataForm';
import { UploadProgressList } from './upload/UploadProgressList';

interface FileUploadProps {
  folderId: string;
  /**
   * Arquivos já fornecidos (ex.: vindos de drag & drop sobre o explorer).
   * - 1 arquivo: pula a dropzone e abre direto o formulário de metadados.
   * - 2+ arquivos: dispara uploads em lote com defaults (título = nome do
   *   arquivo sem extensão), sem pedir metadata individual.
   */
  initialFiles?: globalThis.File[];
  onClose?: () => void;
}

function defaultTitleFor(file: globalThis.File): string {
  return file.name.replace(/\.[^/.]+$/, '');
}

export function FileUpload({ folderId, initialFiles, onClose }: FileUploadProps) {
  const { t } = useTranslation('files');
  const { uploadFile, uploadingFiles, isUploading } = useUpload({
    folderId,
    onSuccess: onClose,
  });

  const hasSingleInitial = initialFiles && initialFiles.length === 1;
  const hasMultipleInitial = !!initialFiles && initialFiles.length > 1;

  const [selectedFile, setSelectedFile] = useState<globalThis.File | null>(
    hasSingleInitial ? initialFiles[0] : null,
  );

  // Bulk upload: dispara N uploads em sequência (no mount) com defaults.
  // useRef impede re-disparo se o componente re-renderizar.
  const bulkStartedRef = useRef(false);
  useEffect(() => {
    if (!hasMultipleInitial || bulkStartedRef.current) return;
    bulkStartedRef.current = true;
    for (const file of initialFiles!) {
      uploadFile(file, { title: defaultTitleFor(file) });
    }
  }, [hasMultipleInitial, initialFiles, uploadFile]);

  const handleCancel = () => {
    setSelectedFile(null);
    onClose?.();
  };

  const handleSubmit = (metadata: UploadMetadata) => {
    if (!selectedFile) return;
    uploadFile(selectedFile, metadata);
  };

  const showProgress = uploadingFiles.length > 0;
  const showDropzone = !showProgress && !selectedFile;
  const showForm = !showProgress && !!selectedFile;

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-foreground">{t('uploadFiles')}</h3>
        {onClose && (
          <IconButton
            variant="ghost"
            size="sm"
            onClick={handleCancel}
            label={t('close', { defaultValue: 'Fechar' })}
            icon={<X className="w-5 h-5" />}
          />
        )}
      </div>

      {showDropzone && (
        <UploadDropzone onSelect={setSelectedFile} disabled={isUploading} />
      )}

      {showForm && selectedFile && (
        <UploadMetadataForm
          file={selectedFile}
          onSubmit={handleSubmit}
          onCancel={handleCancel}
        />
      )}

      {showProgress && <UploadProgressList files={uploadingFiles} />}
    </div>
  );
}
